import React, { useState } from "react";
import { createPlan } from "@api/subscription/plan";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  InputNumber,
  Select,
  Switch,
  Tabs,
  Card,
  Divider,
  Space,
  Tag,
} from "antd";
import { withRouter } from "react-router";
import { SuccessNotification, ErrorNotification } from "@components";
import {
  FiArrowLeft,
  FiSave,
  FiDollarSign,
  FiSettings,
  FiDatabase,
  FiCpu,
  FiShield,
  FiHeadphones,
  FiLayout,
  FiBarChart2,
  FiPlus,
} from "react-icons/fi";

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

// AI models available for Copilotly
const AI_MODELS = [
  "gpt-3.5-turbo",
  "gpt-4",
  "gpt-4-turbo",
  "gpt-4o",
  "claude-3-opus",
  "claude-3-sonnet",
  "claude-3-haiku",
];

// AI features
const AI_FEATURES = [
  "code-generation",
  "translation",
  "summarization",
  "grammar-check",
  "rewriting",
  "content-creation",
];

// Support channels
const SUPPORT_CHANNELS = ["email", "chat", "phone", "slack", "video"];

const initialState = {
  // Basic
  name: "",
  metaDescription: "",
  detailedDescription: "",
  planType: "starter",
  tier: 1,
  isPopular: false,
  isEnterprise: false,

  // Pricing
  pricing: {
    currency: "USD",
    weekly: 0,
    monthly: 0,
    annual: 0,
    lifetime: null,
    perUnit: {
      enabled: false,
      unitName: "credit",
      unitPrice: 0,
      includedUnits: 0,
    },
  },

  // Stripe
  stripe: {
    productId: "",
    weeklyPriceId: "",
    monthlyPriceId: "",
    monthlyMeteredPriceId: "",
    annualPriceId: "",
    annualMeteredPriceId: "",
    lifetimePriceId: "",
  },

  // Trial
  trial: {
    enabled: true,
    days: 14,
    requiresCard: false,
  },

  // Global Limits
  globalLimits: {
    maxCopilots: 1,
    maxCreditsPerMonth: 1000,
    maxTeamMembers: 1,
    maxWorkspaces: 1,
    maxStorageMB: 100,
    maxFileUploadMB: 5,
    maxApiCalls: 10000,
    additionalPricePerCredit: 0,
    dataRetentionDays: 30,
  },

  // AI
  ai: {
    enabled: false,
    maxTokensPerMonth: 0,
    models: [],
    features: [],
  },

  // Features display
  features: [],

  // Support
  support: {
    level: "email",
    responseTime: "48 hours",
    channels: [],
    dedicatedManager: false,
    onboarding: false,
    training: false,
    sla: "",
  },

  // Branding
  branding: {
    removeBranding: false,
    customDomain: false,
    whiteLabel: false,
    customCSS: false,
    customEmails: false,
  },

  // Analytics
  analytics: {
    basicAnalytics: true,
    advancedAnalytics: false,
    exportData: false,
    apiAccess: false,
    customReports: false,
    realtime: false,
  },

  // Security
  security: {
    sso: false,
    twoFactor: true,
    auditLogs: false,
    ipWhitelist: false,
    encryption: true,
    gdprCompliant: true,
    hipaaCompliant: false,
    soc2Compliant: false,
  },

  // Status
  status: "inactive",
  visibility: "private",
  displayOrder: 0,
};

const PlanCreateForm = ({ history }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [featureInput, setFeatureInput] = useState({ name: "", description: "", category: "core" });

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Transform features array
      const featuresArray = values.featuresList || [];

      // Convert pricing from dollars to cents
      const pricingWeekly = Math.round((values.pricing?.weekly || 0) * 100);
      const pricingMonthly = Math.round((values.pricing?.monthly || 0) * 100);
      const pricingAnnual = Math.round((values.pricing?.annual || 0) * 100);

      const planData = {
        ...values,
        pricing: {
          ...values.pricing,
          weekly: pricingWeekly,
          monthly: pricingMonthly,
          annual: pricingAnnual,
          lifetime: values.pricing?.lifetime ? Math.round(values.pricing.lifetime * 100) : null,
        },
        // Also set legacy fields for backward compatibility
        weeklyPrice: pricingWeekly,
        monthlyPrice: pricingMonthly,
        annualPrice: pricingAnnual,
        stripeProductId: values.stripe?.productId,
        stripeWeeklyPriceId: values.stripe?.weeklyPriceId,
        stripeMonthlyPriceId: values.stripe?.monthlyPriceId,
        stripeMonthlyMeteredPriceId: values.stripe?.monthlyMeteredPriceId,
        stripeAnnualPriceId: values.stripe?.annualPriceId,
        stripeAnnualMeteredPriceId: values.stripe?.annualMeteredPriceId,
        maxCopilots: values.globalLimits?.maxCopilots,
        maxCreditsPerMonth: values.globalLimits?.maxCreditsPerMonth,
        trialDays: values.trial?.days,
        features: featuresArray.map(f => ({
          name: f.name,
          description: f.description || "",
          category: f.category || "core",
          included: true,
          highlight: false,
        })),
      };

      // Remove the temporary field
      delete planData.featuresList;

      const { data } = await createPlan(planData);
      SuccessNotification(`${data.name} created successfully`);
      history.push("/super-admin/plans");
    } catch (err) {
      ErrorNotification(err.response?.data?.message || "Error creating plan");
      console.error("CREATE_PLAN_ERROR", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = () => {
    if (!featureInput.name) return;
    const currentFeatures = form.getFieldValue("featuresList") || [];
    form.setFieldsValue({
      featuresList: [...currentFeatures, { ...featureInput }],
    });
    setFeatureInput({ name: "", description: "", category: "core" });
  };

  const handleRemoveFeature = (index) => {
    const currentFeatures = form.getFieldValue("featuresList") || [];
    form.setFieldsValue({
      featuresList: currentFeatures.filter((_, i) => i !== index),
    });
  };

  return (
    <div style={{ padding: "24px", background: "#f8fafc", minHeight: "100vh" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button
              icon={<FiArrowLeft />}
              onClick={() => history.push("/super-admin/plans")}
            >
              Back
            </Button>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Create New Plan</h1>
              <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
                Configure pricing, features, and credit allocations
              </p>
            </div>
          </div>
          <Space>
            <Button onClick={() => form.resetFields()}>Reset</Button>
            <Button
              type="primary"
              icon={<FiSave />}
              loading={loading}
              onClick={() => form.submit()}
              style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
            >
              Create Plan
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          initialValues={initialState}
          onFinish={handleSubmit}
        >
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab={<span><FiSettings /> Basic Info</span>} key="basic">
              <Row gutter={24}>
                <Col span={16}>
                  <Card title="Plan Details" style={{ marginBottom: 24 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="name"
                          label="Plan Name"
                          rules={[{ required: true, message: "Plan name is required" }]}
                        >
                          <Input placeholder="e.g., Professional" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="planType" label="Plan Type">
                          <Select>
                            <Option value="free">Free</Option>
                            <Option value="optimizer">Optimizer</Option>
                            <Option value="wizard">Wizard</Option>
                            <Option value="maverick">Maverick</Option>
                            <Option value="custom">Custom</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="metaDescription"
                      label="Short Description"
                      rules={[{ required: true, message: "Description is required" }]}
                    >
                      <Input placeholder="Brief description for pricing cards" />
                    </Form.Item>

                    <Form.Item name="detailedDescription" label="Detailed Description">
                      <TextArea rows={3} placeholder="Full description with features" />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="tier" label="Tier Level">
                          <InputNumber min={0} max={10} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="displayOrder" label="Display Order">
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="status" label="Status">
                          <Select>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                            <Option value="coming_soon">Coming Soon</Option>
                            <Option value="deprecated">Deprecated</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                <Col span={8}>
                  <Card title="Settings" style={{ marginBottom: 24 }}>
                    <Form.Item name="visibility" label="Visibility">
                      <Select>
                        <Option value="public">Public</Option>
                        <Option value="private">Private</Option>
                        <Option value="hidden">Hidden</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item name="isPopular" label="Mark as Popular" valuePropName="checked">
                      <Switch />
                    </Form.Item>

                    <Form.Item name="isEnterprise" label="Enterprise Plan" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Card>

                  <Card title="Trial Settings">
                    <Form.Item name={["trial", "enabled"]} label="Enable Trial" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["trial", "days"]} label="Trial Days">
                      <InputNumber min={0} max={90} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name={["trial", "requiresCard"]} label="Require Card" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={<span><FiDollarSign /> Pricing</span>} key="pricing">
              <Row gutter={24}>
                <Col span={12}>
                  <Card title="Pricing Configuration" style={{ marginBottom: 24 }}>
                    <Form.Item name={["pricing", "currency"]} label="Currency">
                      <Select>
                        <Option value="USD">USD ($)</Option>
                        <Option value="EUR">EUR (€)</Option>
                        <Option value="GBP">GBP (£)</Option>
                        <Option value="INR">INR (₹)</Option>
                      </Select>
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name={["pricing", "weekly"]} label="Weekly Price">
                          <InputNumber min={0} precision={2} style={{ width: "100%" }} prefix="$" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={["pricing", "monthly"]} label="Monthly Price">
                          <InputNumber min={0} precision={2} style={{ width: "100%" }} prefix="$" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name={["pricing", "annual"]} label="Annual Price">
                          <InputNumber min={0} precision={2} style={{ width: "100%" }} prefix="$" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item name={["pricing", "lifetime"]} label="Lifetime Price (optional)">
                      <InputNumber min={0} precision={2} style={{ width: "100%" }} placeholder="Leave empty if not applicable" />
                    </Form.Item>

                    <Divider>Credit-Based Pricing</Divider>

                    <Form.Item name={["pricing", "perUnit", "enabled"]} label="Enable Per-Credit Pricing" valuePropName="checked">
                      <Switch />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name={["pricing", "perUnit", "unitName"]} label="Unit Name">
                          <Input placeholder="e.g., credit" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name={["pricing", "perUnit", "unitPrice"]} label="Price per Credit">
                          <InputNumber min={0} precision={4} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item name={["pricing", "perUnit", "includedUnits"]} label="Included Credits">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Card>
                </Col>

                <Col span={12}>
                  <Card title="Stripe Integration">
                    <Form.Item name={["stripe", "productId"]} label="Stripe Product ID">
                      <Input placeholder="prod_xxxxx" />
                    </Form.Item>
                    <Form.Item name={["stripe", "weeklyPriceId"]} label="Weekly Price ID">
                      <Input placeholder="price_xxxxx" />
                    </Form.Item>
                    <Form.Item name={["stripe", "monthlyPriceId"]} label="Monthly Price ID">
                      <Input placeholder="price_xxxxx" />
                    </Form.Item>
                    <Form.Item name={["stripe", "monthlyMeteredPriceId"]} label="Monthly Metered Price ID">
                      <Input placeholder="price_xxxxx" />
                    </Form.Item>
                    <Form.Item name={["stripe", "annualPriceId"]} label="Annual Price ID">
                      <Input placeholder="price_xxxxx" />
                    </Form.Item>
                    <Form.Item name={["stripe", "annualMeteredPriceId"]} label="Annual Metered Price ID">
                      <Input placeholder="price_xxxxx" />
                    </Form.Item>
                    <Form.Item name={["stripe", "lifetimePriceId"]} label="Lifetime Price ID">
                      <Input placeholder="price_xxxxx" />
                    </Form.Item>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={<span><FiDatabase /> Limits</span>} key="limits">
              <Card title="Global Limits">
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "maxCopilots"]} label="Max Copilots">
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "maxCreditsPerMonth"]} label="Max Credits/Month">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "maxTeamMembers"]} label="Max Team Members">
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "maxWorkspaces"]} label="Max Workspaces">
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "maxStorageMB"]} label="Max Storage (MB)">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "maxFileUploadMB"]} label="Max File Upload (MB)">
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "maxApiCalls"]} label="Max API Calls/Month">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "additionalPricePerCredit"]} label="Additional Price/Credit">
                      <InputNumber min={0} precision={4} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["globalLimits", "dataRetentionDays"]} label="Data Retention (Days)">
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </TabPane>

            <TabPane tab={<span><FiCpu /> AI Features</span>} key="ai">
              <Card title="AI Configuration">
                <Form.Item name={["ai", "enabled"]} label="Enable AI Features" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name={["ai", "maxTokensPerMonth"]} label="Max Tokens/Month">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name={["ai", "models"]} label="Available Models">
                  <Select mode="multiple" placeholder="Select AI models">
                    {AI_MODELS.map((model) => (
                      <Option key={model} value={model}>{model}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name={["ai", "features"]} label="AI Features">
                  <Select mode="multiple" placeholder="Select AI features">
                    {AI_FEATURES.map((feature) => (
                      <Option key={feature} value={feature}>{feature}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Card>
            </TabPane>

            <TabPane tab={<span><FiHeadphones /> Support</span>} key="support">
              <Row gutter={24}>
                <Col span={12}>
                  <Card title="Support Configuration">
                    <Form.Item name={["support", "level"]} label="Support Level">
                      <Select>
                        <Option value="community">Community</Option>
                        <Option value="email">Email</Option>
                        <Option value="priority">Priority</Option>
                        <Option value="dedicated">Dedicated</Option>
                        <Option value="24/7">24/7</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item name={["support", "responseTime"]} label="Response Time">
                      <Input placeholder="e.g., 48 hours" />
                    </Form.Item>

                    <Form.Item name={["support", "channels"]} label="Support Channels">
                      <Select mode="multiple" placeholder="Select channels">
                        {SUPPORT_CHANNELS.map((channel) => (
                          <Option key={channel} value={channel}>{channel}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item name={["support", "sla"]} label="SLA">
                      <Input placeholder="e.g., 99.9%" />
                    </Form.Item>
                  </Card>
                </Col>

                <Col span={12}>
                  <Card title="Support Options">
                    <Form.Item name={["support", "dedicatedManager"]} label="Dedicated Manager" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["support", "onboarding"]} label="Onboarding" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["support", "training"]} label="Training" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={<span><FiLayout /> Branding</span>} key="branding">
              <Card title="Branding & Customization">
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item name={["branding", "removeBranding"]} label="Remove Branding" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["branding", "customDomain"]} label="Custom Domain" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["branding", "whiteLabel"]} label="White Label" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={["branding", "customCSS"]} label="Custom CSS" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["branding", "customEmails"]} label="Custom Emails" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </TabPane>

            <TabPane tab={<span><FiBarChart2 /> Analytics</span>} key="analytics">
              <Card title="Analytics & Reporting">
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item name={["analytics", "basicAnalytics"]} label="Basic Analytics" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["analytics", "advancedAnalytics"]} label="Advanced Analytics" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["analytics", "exportData"]} label="Export Data" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={["analytics", "apiAccess"]} label="API Access" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["analytics", "customReports"]} label="Custom Reports" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["analytics", "realtime"]} label="Real-time Analytics" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </TabPane>

            <TabPane tab={<span><FiShield /> Security</span>} key="security">
              <Card title="Security & Compliance">
                <Row gutter={24}>
                  <Col span={8}>
                    <Form.Item name={["security", "sso"]} label="SSO" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["security", "twoFactor"]} label="Two-Factor Auth" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["security", "auditLogs"]} label="Audit Logs" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["security", "ipWhitelist"]} label="IP Whitelist" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["security", "encryption"]} label="Encryption" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name={["security", "gdprCompliant"]} label="GDPR Compliant" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["security", "hipaaCompliant"]} label="HIPAA Compliant" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name={["security", "soc2Compliant"]} label="SOC2 Compliant" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </TabPane>

            <TabPane tab={<span><FiPlus /> Features List</span>} key="features">
              <Card title="Display Features">
                <p style={{ color: "#64748b", marginBottom: 16 }}>
                  Add features that will be displayed on the pricing page
                </p>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <Input
                      placeholder="Feature name"
                      value={featureInput.name}
                      onChange={(e) => setFeatureInput({ ...featureInput, name: e.target.value })}
                    />
                  </Col>
                  <Col span={8}>
                    <Input
                      placeholder="Description (optional)"
                      value={featureInput.description}
                      onChange={(e) => setFeatureInput({ ...featureInput, description: e.target.value })}
                    />
                  </Col>
                  <Col span={6}>
                    <Select
                      value={featureInput.category}
                      onChange={(value) => setFeatureInput({ ...featureInput, category: value })}
                      style={{ width: "100%" }}
                    >
                      <Option value="core">Core</Option>
                      <Option value="ai">AI</Option>
                      <Option value="integrations">Integrations</Option>
                      <Option value="support">Support</Option>
                      <Option value="analytics">Analytics</Option>
                    </Select>
                  </Col>
                  <Col span={2}>
                    <Button type="primary" icon={<FiPlus />} onClick={handleAddFeature}>
                      Add
                    </Button>
                  </Col>
                </Row>

                <Form.Item name="featuresList" noStyle>
                  <div>
                    {(form.getFieldValue("featuresList") || []).map((feature, index) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() => handleRemoveFeature(index)}
                        style={{ marginBottom: 8, padding: "4px 8px" }}
                      >
                        <strong>{feature.name}</strong>
                        {feature.description && ` - ${feature.description}`}
                        <span style={{ color: "#64748b", marginLeft: 8 }}>({feature.category})</span>
                      </Tag>
                    ))}
                  </div>
                </Form.Item>
              </Card>
            </TabPane>
          </Tabs>
        </Form>
      </div>
    </div>
  );
};

export default withRouter(PlanCreateForm);
